import {
  AddIcon,
  DeleteIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  CloseIcon,
} from '@chakra-ui/icons';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  VStack,
  HStack,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useToast,
  Select,
  Card,
  CardBody,
  Heading,
  Text,
  Badge,
  SimpleGrid,
  Divider,
  Flex,
  Switch,
  useColorModeValue,
  Spinner,
  Tag,
  TagLabel,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { isSoloLectura } from 'utils/permissions';

interface Hotel {
  Id: number | string;
  Name: string;
}

interface SigningUser {
  [x: string]: any;
  id: string;
  Id?: string;
  name: string;
  Email: string;
  NombreDepartamento: string;
  NombreUsuario: string;
  puesto: string;
  usuarioId: string;
  role: string;
}

interface FlowStep {
  id: string;
  stepNumber: number;
  userId: string | null;
  userName: string;
  userRole: string;
  isRequired: boolean;
  correo?: string;
}

interface SigningFlow {
  id: string;
  name: string;
  description: string;
  department: string;
  hotelId: string;
  hotelName?: string;
  steps: FlowStep[];
  isActive: boolean;
  tipo: 'baja' | 'salida' | 'ambos';
}

const TIPO_COLORS: Record<string, string> = {
  baja: 'red',
  salida: 'orange',
  ambos: 'blue',
};

const TIPO_LABELS: Record<string, string> = {
  baja: 'Baja',
  salida: 'Salida',
  ambos: 'Ambos',
};

const ADMIN_DEPTS = [4, 10];

export default function ConfigFlujosFirma() {
  const [users, setUsers] = useState<SigningUser[]>([]);
  const [flows, setFlows] = useState<SigningFlow[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingFlows, setIsLoadingFlows] = useState(false);
  const [departments, setDepartments] = useState<any[]>([]);
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [selectedHotelId, setSelectedHotelId] = useState<string>('');
  const [selectedDeptFilter, setSelectedDeptFilter] = useState<string>('');
  const [selectedFlow, setSelectedFlow] = useState<SigningFlow | null>(null);
  const [flowFormData, setFlowFormData] = useState({
    name: '',
    description: '',
    department: '',
    hotelId: '',
    tipo: 'ambos' as 'baja' | 'salida' | 'ambos',
  });
  const [stepOrder, setStepOrder] = useState<FlowStep[]>([]);
  const [manualFirmante, setManualFirmante] = useState({ nombre: '', puesto: '', correo: '' });

  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const cardBg = useColorModeValue('white', 'gray.800');

  const { isOpen: isFlowModalOpen, onOpen: onFlowModalOpen, onClose: onFlowModalClose } = useDisclosure();
  const { isOpen: isFlowConfigModalOpen, onOpen: onFlowConfigModalOpen, onClose: onFlowConfigModalClose } = useDisclosure();
  const toast = useToast();

  const isAdmin = () => {
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    return ADMIN_DEPTS.includes(Number(user.DepartamentoId));
  };

  const miDepartamentoId = () => {
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    return String(user.DepartamentoId || '');
  };

  const fetchHotels = async () => {
    try {
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      const res = await fetch('https://inventariosbackend.velasresorts.com/inventarios/hoteles/listado');
      const raw = await res.json();
      // Backend puede devolver [rows, fields] (MySQL2) o directamente rows[]
      const list: Hotel[] = Array.isArray(raw) && Array.isArray(raw[0]) ? raw[0] : (Array.isArray(raw) ? raw : (raw.data || []));

      // Filtrar siempre por los hoteles asignados al usuario
      const allowed = user.HotelIds
        ? String(user.HotelIds).split(',').map((s: string) => s.trim())
        : [];
      const filtered = allowed.length > 0
        ? list.filter(h => allowed.includes(String(h.Id)))
        : list;

      setHotels(filtered);
      if (filtered.length > 0) {
        setSelectedHotelId(String(filtered[0].Id));
      }
    } catch {
      toast({ title: 'Error al cargar hoteles', status: 'error', duration: 3000 });
    }
  };

  const fetchUsers = async (hotelId?: string) => {
    try {
      const hotel = hotelId ?? selectedHotelId;
      if (!hotel) return;
      const response = await fetch(
        `https://inventariosbackend.velasresorts.com/inventarios/usuarios/listado-departamento/${hotel}`
      );
      if (!response.ok) throw new Error();
      const data = await response.json();
      setUsers(data.data);
    } catch {
      toast({ title: 'Error al cargar usuarios', status: 'error', duration: 3000 });
    }
  };

  const fetchFlows = async (hotelId?: string) => {
    const hotel = hotelId ?? selectedHotelId;
    if (!hotel) return;
    try {
      setIsLoadingFlows(true);
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      const url = ADMIN_DEPTS.includes(Number(user.DepartamentoId))
        ? `https://inventariosbackend.velasresorts.com/inventarios/config/flujos/hotel/${hotel}`
        : `https://inventariosbackend.velasresorts.com/inventarios/config/flujos/hotel/${hotel}/departamento/${user.DepartamentoId}`;

      const response = await fetch(url);
      if (!response.ok) throw new Error();
      const data = await response.json();

      let rows: any[] = [];
      if (data.success && data.data) {
        rows = Array.isArray(data.data) ? data.data : [data.data];
      } else if (Array.isArray(data)) {
        rows = data;
      }

      const byFlujo: Record<string, any[]> = {};
      rows.forEach(row => {
        const k = row.flujoId ? String(row.flujoId) : `dept-${row.departamentoId}`;
        if (!byFlujo[k]) byFlujo[k] = [];
        byFlujo[k].push(row);
      });

      const mapped: SigningFlow[] = Object.entries(byFlujo).map(([flujoKey, rows]) => {
        const sorted = [...rows].sort((a, b) => a.orden - b.orden);
        const first = sorted[0];
        return {
          id: flujoKey,
          name: first.nombreFlujo || first.NombreFlujo || `Flujo ${first.NombreDepartamento || flujoKey}`,
          description: first.descripcion || '',
          department: first.NombreDepartamento || String(first.departamentoId),
          hotelId: String(first.hotelId || hotel),
          hotelName: first.NombreHotel || hotels.find(h => String(h.Id) === hotel)?.Name || '',
          isActive: true,
          tipo: (first.tipo || 'ambos') as 'baja' | 'salida' | 'ambos',
          steps: sorted.map((r, i) => ({
            id: String(r.id),
            stepNumber: i + 1,
            userId: r.usuarioId ? String(r.usuarioId) : null,
            userName: r.NombreUsuario || r.puesto || '',
            userRole: r.NombreUsuario ? (r.puesto || '') : 'EXTERNO',
            isRequired: r.firmo === 1,
            correo: r.comentario !== 'N/A' ? r.comentario : undefined,
          })),
        };
      });

      setFlows(mapped);
    } catch {
      // silent — puede que no haya flujos configurados aún
    } finally {
      setIsLoadingFlows(false);
    }
  };

  // Initial load
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const user = JSON.parse(sessionStorage.getItem('user') || '{}');
        if (!ADMIN_DEPTS.includes(Number(user.DepartamentoId))) {
          const res = await fetch(`https://inventariosbackend.velasresorts.com/inventarios/departamentos/${user.DepartamentoId}`);
          const data = await res.json();
          setDepartments([data]);
        } else {
          const res = await fetch('https://inventariosbackend.velasresorts.com/inventarios/departamentos/listado');
          const data = await res.json();
          setDepartments(data[0]);
        }
      } catch {
        console.error('Error cargando departamentos');
      }
    };

    fetchHotels();
    fetchDepartments();
  }, []);

  // Reload flows and users when hotel changes
  useEffect(() => {
    if (selectedHotelId) {
      fetchFlows(selectedHotelId);
      fetchUsers(selectedHotelId);
      setSelectedDeptFilter('');
    }
  }, [selectedHotelId]);

  // ── Guardar orden desde modal de configuración ────────────────────────────

  const handleSaveFlowConfiguration = async () => {
    if (!selectedFlow) return;
    try {
      setIsSaving(true);
      const isNewStep = (id: string) => id.startsWith('manual-') || id.startsWith('self-');

      for (const step of stepOrder) {
        if (!isNewStep(step.id)) continue;
        const idx = stepOrder.indexOf(step);
        const res = await fetch('https://inventariosbackend.velasresorts.com/inventarios/config/flujos/agregar-paso', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ flujoId: selectedFlow.id, step: { ...step, stepNumber: idx + 1 } }),
        });
        if (!res.ok) throw new Error();
      }

      const existingSteps = stepOrder.filter(s => !isNewStep(s.id));
      if (existingSteps.length > 0) {
        const res = await fetch('https://inventariosbackend.velasresorts.com/inventarios/config/flujos/actualizar-orden', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pasos: existingSteps.map(s => ({ id: s.id, orden: stepOrder.indexOf(s) + 1 })) }),
        });
        if (!res.ok) throw new Error();
      }

      toast({ title: 'Flujo actualizado', status: 'success', duration: 2000 });
      await fetchFlows();
      onFlowConfigModalClose();
    } catch {
      toast({ title: 'Error al guardar', status: 'error', duration: 3000 });
    } finally {
      setIsSaving(false);
    }
  };

  // ── Crear / guardar flujo ─────────────────────────────────────────────────

  const handleAddFlow = () => {
    setSelectedFlow(null);
    const me = JSON.parse(sessionStorage.getItem('user') || '{}');
    // Un flujo siempre se crea para el departamento de quien lo crea — el campo
    // queda fijo (ver Select de Departamento), sin importar si es admin o no.
    setFlowFormData({ name: '', description: '', department: miDepartamentoId(), hotelId: selectedHotelId, tipo: 'ambos' });

    const firstStep: FlowStep = {
      id: `self-${Date.now()}`,
      stepNumber: 1,
      userId: me.Id ? String(me.Id) : null,
      userName: me.FullName || me.NombreUsuario || '',
      userRole: 'SOLICITANTE',
      isRequired: true,
      correo: me.Email || '',
    };
    setStepOrder([firstStep]);
    setManualFirmante({ nombre: '', puesto: '', correo: '' });
    onFlowModalOpen();
  };

  const handleSaveFlow = async () => {
    if (!flowFormData.name || !flowFormData.hotelId || stepOrder.length === 0) {
      toast({ title: 'Completa nombre, hotel y al menos un firmante', status: 'warning', duration: 3000 });
      return;
    }
    const body = {
      ...flowFormData,
      steps: stepOrder.map((s, i) => ({ ...s, stepNumber: i + 1, departmentId: flowFormData.department })),
    };
    try {
      const res = await fetch('https://inventariosbackend.velasresorts.com/inventarios/config/flujos/crear', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error();
      toast({ title: 'Flujo creado', status: 'success', duration: 2000 });
      await fetchFlows();
    } catch {
      toast({ title: 'Error al crear flujo', status: 'error', duration: 3000 });
    }
    onFlowModalClose();
  };

  // ── Eliminar flujo ────────────────────────────────────────────────────────

  const handleDeleteFlow = async (id: string) => {
    if (!window.confirm('¿Eliminar este flujo de firmas?')) return;
    try {
      await fetch(`https://inventariosbackend.velasresorts.com/inventarios/config/flujos/${id}`, { method: 'DELETE' });
      setFlows(flows.filter(f => f.id !== id));
      toast({ title: 'Flujo eliminado', status: 'info', duration: 2000 });
    } catch {
      toast({ title: 'Error al eliminar', status: 'error', duration: 3000 });
    }
  };

  const handleToggleFlowStatus = (id: string) =>
    setFlows(flows.map(f => f.id === id ? { ...f, isActive: !f.isActive } : f));

  // ── Configurar flujo existente ────────────────────────────────────────────

  const handleConfigureFlow = (flow: SigningFlow) => {
    setSelectedFlow(flow);
    setStepOrder([...flow.steps]);
    setManualFirmante({ nombre: '', puesto: '', correo: '' });
    onFlowConfigModalOpen();
  };

  // ── Gestión de pasos ──────────────────────────────────────────────────────

  const handleAddManualToFlow = () => {
    const { nombre, puesto, correo } = manualFirmante;
    if (!nombre.trim()) return;
    const newStep: FlowStep = {
      id: `manual-${Date.now()}`,
      stepNumber: 0,
      userId: null,
      userName: nombre.trim(),
      userRole: puesto.trim() || 'EXTERNO',
      isRequired: true,
      correo: correo.trim() || undefined,
    };
    setStepOrder(prev => [...prev, { ...newStep, stepNumber: prev.length + 1 }]);
    setManualFirmante({ nombre: '', puesto: '', correo: '' });
  };

  const handleRemoveUserFromFlow = (stepId: string) => {
    setStepOrder(prev => prev.filter(s => s.id !== stepId).map((s, i) => ({ ...s, stepNumber: i + 1 })));
  };

  const handleUpdateStepCorreo = (stepId: string, correo: string) => {
    setStepOrder(prev => prev.map(s => s.id === stepId ? { ...s, correo } : s));
  };

  const handleMoveStep = (stepId: string, direction: 'up' | 'down') => {
    setStepOrder(prev => {
      const idx = prev.findIndex(s => s.id === stepId);
      if ((direction === 'up' && idx === 0) || (direction === 'down' && idx === prev.length - 1)) return prev;
      const arr = [...prev];
      const next = direction === 'up' ? idx - 1 : idx + 1;
      [arr[idx], arr[next]] = [arr[next], arr[idx]];
      return arr.map((s, i) => ({ ...s, stepNumber: i + 1 }));
    });
  };

  // ── Filtrado ──────────────────────────────────────────────────────────────

  const visibleFlows = selectedDeptFilter
    ? flows.filter(f => f.department === selectedDeptFilter || String(f.department) === String(selectedDeptFilter))
    : flows;

  const selectedHotelName = hotels.find(h => String(h.Id) === selectedHotelId)?.Name || selectedHotelId;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box p={6} maxW="1400px" mx="auto" mt="5rem">
      <VStack spacing={8} align="stretch">

        {/* Header */}
        <Box>
          <Heading size="lg" fontWeight="600">Flujos de Firma</Heading>
          <Text color="gray.500" mt={1} fontSize="sm">
            Configura quién firma y en qué orden para bajas y salidas, por hotel y departamento
          </Text>
        </Box>

        {/* Hotel selector */}
        <Box>
          <Text fontSize="xs" fontWeight="600" color="gray.500" mb={2} textTransform="uppercase" letterSpacing="wide">
            Hotel
          </Text>
          {hotels.length === 0 ? (
            <HStack><Spinner size="sm" /><Text fontSize="sm" color="gray.400">Cargando hoteles...</Text></HStack>
          ) : (
            <Wrap spacing={2}>
              {hotels.map(h => (
                <WrapItem key={h.Id}>
                  <Button
                    size="sm"
                    variant={selectedHotelId === String(h.Id) ? 'solid' : 'outline'}
                    colorScheme="blue"
                    borderRadius="full"
                    onClick={() => setSelectedHotelId(String(h.Id))}
                  >
                    {h.Name}
                  </Button>
                </WrapItem>
              ))}
            </Wrap>
          )}
        </Box>

        {/* Flujos */}
        {selectedHotelId && (
          <Box>
            <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
              <Box>
                <Heading size="md" fontWeight="600">{selectedHotelName}</Heading>
                {isAdmin() && departments.length > 1 && (
                  <HStack mt={2} spacing={2}>
                    <Select
                      size="xs"
                      maxW="220px"
                      placeholder="Todos los departamentos"
                      value={selectedDeptFilter}
                      onChange={e => setSelectedDeptFilter(e.target.value)}
                    >
                      {departments.map((d: any) => (
                        <option key={d.Id} value={d.Name}>{d.Name}</option>
                      ))}
                    </Select>
                  </HStack>
                )}
              </Box>
              {!isSoloLectura() && (
                <Button leftIcon={<AddIcon />} colorScheme="blue" variant="outline" size="sm" onClick={handleAddFlow}>
                  Nuevo flujo
                </Button>
              )}
            </Flex>

            {isLoadingFlows ? (
              <Flex justify="center" py={12}><Spinner /></Flex>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={4}>
                {visibleFlows.map(flow => (
                  <Card key={flow.id} variant="outline" borderWidth="1px" borderColor={borderColor} size="sm"
                    _hover={{ borderColor: 'blue.200', shadow: 'sm' }} transition="all 0.2s" bg={cardBg}>
                    <CardBody p={4}>
                      <VStack align="stretch" spacing={3}>
                        <Flex justify="space-between" align="start">
                          <Box flex="1" minW="0">
                            <Heading size="sm" fontWeight="600" noOfLines={1} mb={1}>{flow.name}</Heading>
                            {flow.description && (
                              <Text fontSize="xs" color="gray.500" noOfLines={1}>{flow.description}</Text>
                            )}
                          </Box>
                          <Switch size="sm" isChecked={flow.isActive}
                            onChange={() => handleToggleFlowStatus(flow.id)} colorScheme="green" ml={2}
                            isDisabled={isSoloLectura()} />
                        </Flex>

                        <HStack spacing={2} flexWrap="wrap">
                          <Badge variant="subtle" colorScheme={TIPO_COLORS[flow.tipo] || 'gray'} fontSize="2xs" px={2} py={0.5}>
                            {TIPO_LABELS[flow.tipo] || flow.tipo}
                          </Badge>
                          {flow.department && (
                            <Badge variant="subtle" colorScheme="purple" fontSize="2xs" px={2} py={0.5} maxW="120px" isTruncated>
                              {flow.department}
                            </Badge>
                          )}
                          <Badge variant="subtle" colorScheme={flow.isActive ? 'green' : 'gray'} fontSize="2xs" px={2} py={0.5}>
                            {flow.steps.length} paso{flow.steps.length !== 1 ? 's' : ''}
                          </Badge>
                        </HStack>

                        <VStack align="stretch" spacing={1.5}>
                          {flow.steps.slice(0, 4).map(step => (
                            <Box key={step.id}>
                              <HStack spacing={2}>
                                <Box w="18px" h="18px" borderRadius="sm" bg="gray.100" display="flex"
                                  alignItems="center" justifyContent="center" fontSize="10px"
                                  fontWeight="600" color="gray.600" flexShrink={0}>
                                  {step.stepNumber}
                                </Box>
                                <Text fontSize="xs" noOfLines={1} flex="1" color="gray.700">
                                  {step.userName}
                                </Text>
                                <Text fontSize="10px" color="gray.400" noOfLines={1}>{step.userRole}</Text>
                              </HStack>
                              {step.correo && (
                                <Text fontSize="10px" color="gray.400" noOfLines={1} pl={6}>{step.correo}</Text>
                              )}
                            </Box>
                          ))}
                          {flow.steps.length > 4 && (
                            <Text fontSize="2xs" color="blue.500" pl={6}>+{flow.steps.length - 4} más</Text>
                          )}
                        </VStack>

                        {!isSoloLectura() && (
                          <Flex justify="space-between" pt={2} borderTop="1px" borderColor={borderColor}>
                            <Button size="xs" variant="ghost" colorScheme="blue" onClick={() => handleConfigureFlow(flow)}>
                              Configurar
                            </Button>
                            <IconButton icon={<DeleteIcon />} aria-label="Eliminar flujo" size="xs"
                              variant="ghost" colorScheme="red" onClick={() => handleDeleteFlow(flow.id)} />
                          </Flex>
                        )}
                      </VStack>
                    </CardBody>
                  </Card>
                ))}

                {!isSoloLectura() && (
                  <Card variant="outline" borderWidth="2px" borderStyle="dashed" borderColor="gray.300" size="sm"
                    cursor="pointer" onClick={handleAddFlow} _hover={{ borderColor: 'blue.300', bg: 'blue.50' }} transition="all 0.2s">
                    <CardBody p={4} display="flex" flexDirection="column" alignItems="center" justifyContent="center" minH="160px">
                      <Box w="10" h="10" borderRadius="md" bg="blue.100" display="flex" alignItems="center" justifyContent="center" mb={3}>
                        <AddIcon color="blue.500" />
                      </Box>
                      <Text fontSize="sm" fontWeight="500" color="blue.600">Nuevo flujo</Text>
                      <Text fontSize="xs" color="gray.500" textAlign="center" mt={1}>Crear proceso de firmas</Text>
                    </CardBody>
                  </Card>
                )}
              </SimpleGrid>
            )}
          </Box>
        )}

      </VStack>

      {/* ── Modal: Crear flujo ──────────────────────────────────────────────── */}
      <Modal isOpen={isFlowModalOpen} onClose={onFlowModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px" borderColor="gray.100" fontSize="md">
            Nuevo flujo de firmas
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <VStack spacing={5} align="stretch">
              <SimpleGrid columns={2} spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Nombre del flujo</FormLabel>
                  <Input value={flowFormData.name}
                    onChange={e => setFlowFormData({ ...flowFormData, name: e.target.value })}
                    placeholder="Ej: Flujo salidas TI" size="sm" />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm">Aplica para</FormLabel>
                  <Select value={flowFormData.tipo} size="sm"
                    onChange={e => setFlowFormData({ ...flowFormData, tipo: e.target.value as any })}>
                    <option value="ambos">Bajas y Salidas</option>
                    <option value="baja">Solo Bajas</option>
                    <option value="salida">Solo Salidas</option>
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm">Hotel</FormLabel>
                  <Select value={flowFormData.hotelId} size="sm" placeholder="Seleccionar hotel..."
                    onChange={e => setFlowFormData({ ...flowFormData, hotelId: e.target.value })}>
                    {hotels.map(h => (
                      <option key={h.Id} value={h.Id}>{h.Name}</option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm">Departamento</FormLabel>
                  {/* Un flujo siempre es para el departamento de quien lo crea — nunca para "todos". */}
                  <Select value={flowFormData.department} size="sm" placeholder="Seleccionar..."
                    isDisabled
                    onChange={e => setFlowFormData({ ...flowFormData, department: e.target.value })}>
                    {departments.filter((d: any) => String(d.Id) === miDepartamentoId()).map((d: any) => (
                      <option key={d.Id} value={d.Id}>{d.Name}</option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl gridColumn="span 2">
                  <FormLabel fontSize="sm">Descripción (opcional)</FormLabel>
                  <Input value={flowFormData.description}
                    onChange={e => setFlowFormData({ ...flowFormData, description: e.target.value })}
                    placeholder="Breve descripción" size="sm" />
                </FormControl>
              </SimpleGrid>

              <Divider />

              <Box>
                <Text fontSize="sm" fontWeight="600" mb={3}>Orden de firmantes</Text>

                <Box p={3} border="1px dashed" borderColor={borderColor} borderRadius="8px" mb={3}>
                  <Text fontSize="xs" fontWeight="600" color="gray.500" mb={2}>Agregar firmante</Text>
                  <SimpleGrid columns={3} spacing={2} mb={2}>
                    <Input size="xs" borderRadius="6px" placeholder="Nombre completo *"
                      value={manualFirmante.nombre}
                      onChange={e => setManualFirmante(p => ({ ...p, nombre: e.target.value }))} />
                    <Input size="xs" borderRadius="6px" placeholder="Puesto / Cargo"
                      value={manualFirmante.puesto}
                      onChange={e => setManualFirmante(p => ({ ...p, puesto: e.target.value }))} />
                    <Input size="xs" borderRadius="6px" placeholder="correo@empresa.com" type="email"
                      value={manualFirmante.correo}
                      onChange={e => setManualFirmante(p => ({ ...p, correo: e.target.value }))} />
                  </SimpleGrid>
                  <Button size="xs" leftIcon={<AddIcon />} colorScheme="gray" variant="outline"
                    isDisabled={!manualFirmante.nombre.trim()} onClick={handleAddManualToFlow}>
                    Agregar firmante
                  </Button>
                </Box>

                <VStack align="stretch" spacing={2} maxH="320px" overflowY="auto">
                  {stepOrder.map((step, i) => (
                    <Box key={step.id} px={2.5} py={2} border="1px solid" borderColor={borderColor} borderRadius="8px" bg="gray.50">
                      <HStack spacing={2}>
                        <Badge colorScheme="blue" borderRadius="full" minW="22px" textAlign="center" flexShrink={0}>{i + 1}</Badge>
                        <Box minW={0} flex="1">
                          <Text fontSize="sm" fontWeight="600" noOfLines={1}>{step.userName}</Text>
                          <Text fontSize="10px" color="gray.400">{step.userRole}</Text>
                        </Box>
                        <Input
                          size="xs" borderRadius="6px" placeholder="correo@empresa.com" type="email"
                          value={step.correo || ''}
                          onChange={e => handleUpdateStepCorreo(step.id, e.target.value)}
                          w="160px" flexShrink={0}
                        />
                        <HStack spacing={0} flexShrink={0}>
                          <IconButton size="xs" variant="ghost" icon={<ChevronUpIcon />} aria-label="Subir" isDisabled={i === 0} onClick={() => handleMoveStep(step.id, 'up')} />
                          <IconButton size="xs" variant="ghost" icon={<ChevronDownIcon />} aria-label="Bajar" isDisabled={i === stepOrder.length - 1} onClick={() => handleMoveStep(step.id, 'down')} />
                          <IconButton size="xs" variant="ghost" colorScheme="red" icon={<CloseIcon w={2} h={2} />} aria-label="Quitar" onClick={() => handleRemoveUserFromFlow(step.id)} />
                        </HStack>
                      </HStack>
                    </Box>
                  ))}
                  {stepOrder.length === 0 && (
                    <Box p={6} textAlign="center" border="2px dashed" borderColor="gray.200" borderRadius="8px">
                      <Text fontSize="sm" color="gray.400">Agrega firmantes usando el formulario de arriba</Text>
                    </Box>
                  )}
                </VStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter borderTop="1px" borderColor="gray.100">
            <Button variant="ghost" mr={3} onClick={onFlowModalClose} size="sm">Cancelar</Button>
            <Button colorScheme="blue" size="sm" onClick={handleSaveFlow}
              isDisabled={!flowFormData.name || !flowFormData.hotelId || stepOrder.length === 0}>
              Crear flujo
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Modal: Configurar flujo existente ──────────────────────────────── */}
      <Modal isOpen={isFlowConfigModalOpen} onClose={onFlowConfigModalClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader pb={3} fontSize="md">
            Configurar: <Text as="span" color="blue.500">{selectedFlow?.name}</Text>
            {selectedFlow?.department && (
              <Badge ml={2} colorScheme="purple" fontSize="xs" fontWeight="normal">{selectedFlow.department}</Badge>
            )}
          </ModalHeader>
          <ModalCloseButton size="sm" />
          <ModalBody pb={6}>
            <VStack spacing={5} align="stretch">
              <Box p={3} border="1px dashed" borderColor={borderColor} borderRadius="8px">
                <Text fontSize="xs" fontWeight="600" color="gray.500" mb={2}>Agregar firmante</Text>
                <SimpleGrid columns={3} spacing={2} mb={2}>
                  <Input size="xs" borderRadius="6px" placeholder="Nombre completo *"
                    value={manualFirmante.nombre}
                    onChange={e => setManualFirmante(p => ({ ...p, nombre: e.target.value }))} />
                  <Input size="xs" borderRadius="6px" placeholder="Puesto / Cargo"
                    value={manualFirmante.puesto}
                    onChange={e => setManualFirmante(p => ({ ...p, puesto: e.target.value }))} />
                  <Input size="xs" borderRadius="6px" placeholder="correo@empresa.com" type="email"
                    value={manualFirmante.correo}
                    onChange={e => setManualFirmante(p => ({ ...p, correo: e.target.value }))} />
                </SimpleGrid>
                <Button size="xs" leftIcon={<AddIcon />} colorScheme="gray" variant="outline"
                  isDisabled={!manualFirmante.nombre.trim()} onClick={handleAddManualToFlow}>
                  Agregar firmante
                </Button>
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="500" mb={3}>Orden de firma</Text>
                <VStack spacing={2} align="stretch">
                  {stepOrder.map((step, i) => (
                    <Box key={step.id} px={2.5} py={2} border="1px solid" borderColor={borderColor} borderRadius="8px" bg={cardBg}>
                      <HStack spacing={2}>
                        <HStack spacing={0} flexShrink={0}>
                          <IconButton icon={<ChevronUpIcon />} aria-label="Subir" size="xs" variant="ghost"
                            isDisabled={i === 0} onClick={() => handleMoveStep(step.id, 'up')} />
                          <IconButton icon={<ChevronDownIcon />} aria-label="Bajar" size="xs" variant="ghost"
                            isDisabled={i === stepOrder.length - 1} onClick={() => handleMoveStep(step.id, 'down')} />
                        </HStack>
                        <Box w="7" h="7" borderRadius="sm" bg="blue.50" display="flex" alignItems="center"
                          justifyContent="center" fontSize="xs" fontWeight="600" color="blue.600" flexShrink={0}>
                          {step.stepNumber}
                        </Box>
                        <Box minW={0} flex="1">
                          <Text fontSize="sm" fontWeight="500" noOfLines={1}>{step.userName}</Text>
                          <Text fontSize="10px" color="gray.500">{step.userRole}</Text>
                        </Box>
                        <Input
                          size="xs" borderRadius="6px" placeholder="correo@empresa.com" type="email"
                          value={step.correo || ''}
                          onChange={e => handleUpdateStepCorreo(step.id, e.target.value)}
                          w="160px" flexShrink={0}
                        />
                        <IconButton icon={<CloseIcon w={2} h={2} />} aria-label="Quitar"
                          size="xs" variant="ghost" colorScheme="red" flexShrink={0}
                          onClick={() => handleRemoveUserFromFlow(step.id)} />
                      </HStack>
                    </Box>
                  ))}
                  {stepOrder.length === 0 && (
                    <Box p={6} textAlign="center" border="2px dashed" borderColor="gray.200" borderRadius="8px">
                      <Text fontSize="sm" color="gray.500">No hay firmantes en este flujo</Text>
                    </Box>
                  )}
                </VStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter pt={3} borderTop="1px" borderColor="gray.200">
            <Button variant="ghost" mr={3} onClick={onFlowConfigModalClose} size="sm">Cancelar</Button>
            <Button colorScheme="blue" size="sm" onClick={handleSaveFlowConfiguration}
              isLoading={isSaving} loadingText="Guardando...">
              Guardar cambios
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
